/* ==========================================================================
   EDME — Tabbed Product Carousel
   Click + Drag dengeli final sürüm
   ========================================================================== */

(function () {
  'use strict';

  const SELECTOR = '[data-edme-tabbed-product-carousel]';
  const DRAG_THRESHOLD = 12;

  function getActivePanel(section) {
    return section.querySelector('[data-edme-tpc-panel].is-active');
  }

  function getActiveCarousel(section) {
    const panel = getActivePanel(section);
    return panel ? panel.querySelector('[data-edme-tpc-carousel]') : null;
  }

  function getActiveTrack(section) {
    const carousel = getActiveCarousel(section);
    return carousel ? carousel.querySelector('[data-edme-tpc-track]') : null;
  }

  function getActivePrevBtn(section) {
    const carousel = getActiveCarousel(section);
    return carousel ? carousel.querySelector('[data-edme-tpc-prev]') : null;
  }

  function getActiveNextBtn(section) {
    const carousel = getActiveCarousel(section);
    return carousel ? carousel.querySelector('[data-edme-tpc-next]') : null;
  }

  function getScrollAmount(section, track) {
    const card = track?.querySelector('.edme-tabbed-product-carousel__item');
    const multiplier = parseInt(section.getAttribute('data-scroll-step'), 10) || 2;

    if (!card) return 300 * multiplier;

    const styles = window.getComputedStyle(track);
    const gap = parseFloat(styles.columnGap || styles.gap || 0) || 0;

    return (card.offsetWidth + gap) * multiplier;
  }

  function updateArrows(section) {
    const track = getActiveTrack(section);
    const prevBtn = getActivePrevBtn(section);
    const nextBtn = getActiveNextBtn(section);

    if (!track || !prevBtn || !nextBtn) return;

    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    const currentLeft = track.scrollLeft;
    const threshold = 4;

    const atStart = currentLeft <= threshold;
    const atEnd = currentLeft >= maxScrollLeft - threshold;
    const noOverflow = maxScrollLeft <= threshold;

    prevBtn.disabled = noOverflow || atStart;
    nextBtn.disabled = noOverflow || atEnd;

    prevBtn.classList.toggle('is-disabled', noOverflow || atStart);
    nextBtn.classList.toggle('is-disabled', noOverflow || atEnd);
  }

  function activateTab(section, targetId) {
    const tabs = section.querySelectorAll('[data-edme-tpc-tab-trigger]');
    const panels = section.querySelectorAll('[data-edme-tpc-panel]');

    tabs.forEach((tab) => {
      const isActive = tab.getAttribute('data-target') === targetId;

      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');

      if (isActive) {
        tab.scrollIntoView({
          behavior: 'smooth',
          inline: 'nearest',
          block: 'nearest'
        });
      }
    });

    panels.forEach((panel) => {
      const isActive = panel.getAttribute('data-panel') === targetId;

      panel.classList.toggle('is-active', isActive);

      if (isActive) {
        panel.removeAttribute('hidden');

        const track = panel.querySelector('[data-edme-tpc-track]');
        if (track) track.scrollLeft = 0;
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    window.requestAnimationFrame(() => {
      updateArrows(section);
    });
  }

  function bindDragScroll(section, track) {
    if (!track || track.dataset.dragBound === 'true') return;

    track.dataset.dragBound = 'true';

    let pointerDown = false;
    let isDragging = false;
    let startX = 0;
    let startLeft = 0;
    let activePointerId = null;

    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') return;
      if (event.button !== 0) return;
      if (event.target.closest('button, input, select, textarea')) return;

      pointerDown = true;
      isDragging = false;
      startX = event.clientX;
      startLeft = track.scrollLeft;
      activePointerId = event.pointerId;
    });

    track.addEventListener('pointermove', (event) => {
      if (!pointerDown) return;

      const diff = event.clientX - startX;

      if (!isDragging && Math.abs(diff) < DRAG_THRESHOLD) {
        return;
      }

      if (!isDragging) {
        isDragging = true;
        track.classList.add('is-dragging');
        track.setPointerCapture?.(activePointerId);
      }

      event.preventDefault();
      track.scrollLeft = startLeft - diff;
    });

    const endDrag = (event) => {
      if (!pointerDown) return;

      pointerDown = false;

      if (isDragging) {
        track.dataset.wasDragged = 'true';

        window.setTimeout(() => {
          delete track.dataset.wasDragged;
        }, 0);
      }

      isDragging = false;
      activePointerId = null;
      track.classList.remove('is-dragging');

      try {
        track.releasePointerCapture?.(event.pointerId);
      } catch (err) {}

      window.requestAnimationFrame(() => {
        updateArrows(section);
      });
    };

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('pointerleave', endDrag);

    track.addEventListener(
      'click',
      (event) => {
        if (track.dataset.wasDragged === 'true') {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );
  }

  function bindTrack(section, track) {
    if (!track || track.dataset.scrollBound === 'true') return;

    track.dataset.scrollBound = 'true';

    bindDragScroll(section, track);

    track.addEventListener(
      'scroll',
      () => {
        window.requestAnimationFrame(() => updateArrows(section));
      },
      { passive: true }
    );
  }

  function initSection(section) {
    if (!section || section.dataset.edmeTabbedProductCarouselInit === 'true') return;

    section.dataset.edmeTabbedProductCarouselInit = 'true';

    const tabs = section.querySelectorAll('[data-edme-tpc-tab-trigger]');
    const tracks = section.querySelectorAll('[data-edme-tpc-track]');
    const prevButtons = section.querySelectorAll('[data-edme-tpc-prev]');
    const nextButtons = section.querySelectorAll('[data-edme-tpc-next]');

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const targetId = tab.getAttribute('data-target');
        if (targetId) activateTab(section, targetId);
      });
    });

    tracks.forEach((track) => {
      bindTrack(section, track);
    });

    prevButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const track = getActiveTrack(section);
        if (!track) return;

        track.scrollBy({
          left: -getScrollAmount(section, track),
          behavior: 'smooth'
        });
      });
    });

    nextButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const track = getActiveTrack(section);
        if (!track) return;

        track.scrollBy({
          left: getScrollAmount(section, track),
          behavior: 'smooth'
        });
      });
    });

    window.addEventListener(
      'resize',
      () => {
        window.requestAnimationFrame(() => updateArrows(section));
      },
      { passive: true }
    );

    updateArrows(section);
  }

  function initAll() {
    document.querySelectorAll(SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target;
    const section = root.matches?.(SELECTOR) ? root : root.querySelector(SELECTOR);

    if (section) {
      section.dataset.edmeTabbedProductCarouselInit = 'false';
      initSection(section);
    }
  });
})();

/* ==========================================================================
   EDME — Product Card Image Carousel (kart içi görsel carousel)
   - Masaüstü: görselin üzerinde mouse'u sağa/sola gezdir → görsel değişir
               (hover-zone: görsel, görsel sayısı kadar dikey bölgeye ayrılır)
   - Mobil:    parmakla sağa/sola kaydır → görsel değişir (swipe)
   - Dotlar senkron çalışır; görsel sayısı markup'tan gelir (en fazla 4).
   - Tamamen ayrı selektörler (data-pcard-*) kullanır; yukarıdaki dış
     carousel (data-edme-tpc-*) ile çakışmaz.
   ========================================================================== */
(function () {
  'use strict';

  const CARD = '[data-pcard-carousel]';
  const SWIPE_THRESHOLD = 30; // px — görsel değiştirmek için min. yatay kaydırma

  function initCard(card) {
    if (!card || card.dataset.pcardImgInit === 'true') return;

    const track = card.querySelector('[data-pcard-track]');
    if (!track) {
      card.dataset.pcardImgInit = 'true';
      return;
    }

    const slides = Array.prototype.slice.call(
      track.querySelectorAll('.edme-pcard__slide')
    );

    // Tek görsel varsa carousel'e gerek yok
    if (slides.length <= 1) {
      card.dataset.pcardImgInit = 'true';
      return;
    }

    card.dataset.pcardImgInit = 'true';

    const media = card.querySelector('.edme-pcard__media');
    const prevBtn = card.querySelector('[data-pcard-prev]');
    const nextBtn = card.querySelector('[data-pcard-next]');

    const count = slides.length;
    let current = 0;

    function clamp(i) {
      if (i < 0) return 0;
      if (i > count - 1) return count - 1;
      return i;
    }

    // Başlangıç: sadece ilk slide görünür olsun (kart CSS'inden bağımsız).
    for (let i = 0; i < slides.length; i++) {
      slides[i].style.display = i === 0 ? '' : 'none';
      slides[i].classList.toggle('is-active', i === 0);
    }
    function goTo(index) {
      index = clamp(index);
      if (index === current) return;
      current = index;

      for (let i = 0; i < slides.length; i++) {
        // Görseli CSS'e bağlı kalmadan değiştir: aktif olmayanları gizle.
        slides[i].style.display = i === index ? '' : 'none';
        slides[i].classList.toggle('is-active', i === index);
      }
      // Tembel (lazy) yüklü görseli görünür olunca yüklemeye zorla
      const img = slides[index].querySelector('img');
      if (img && img.getAttribute('loading') === 'lazy') {
        img.setAttribute('loading', 'eager');
      }
    }

    /* === Masaüstü: hover-zone === */
    const canHover =
      window.matchMedia &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    if (canHover && media) {
      media.addEventListener('mousemove', function (event) {
        const rect = media.getBoundingClientRect();
        if (rect.width <= 0) return;

        let ratio = (event.clientX - rect.left) / rect.width;
        if (ratio < 0) ratio = 0;
        if (ratio > 0.999) ratio = 0.999;

        goTo(Math.floor(ratio * count));
      });

      // Mouse görselden çıkınca ilk (ana) görsele dön
      media.addEventListener('mouseleave', function () {
        goTo(0);
      });
    }


    /* === Ok butonları (CSS ile gizli olsa da çalışır) === */
    if (prevBtn) {
      prevBtn.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        goTo(current - 1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        goTo(current + 1);
      });
    }

    /* === Mobil: touch swipe ===
       Yatay hareket görseli değiştirir; dikey hareket sayfayı kaydırır. */
    track.style.touchAction = 'pan-y';

    let startX = 0;
    let startY = 0;
    let touching = false;
    let swiping = false;

    track.addEventListener(
      'touchstart',
      function (event) {
        if (event.touches.length !== 1) return;
        touching = true;
        swiping = false;
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
      },
      { passive: true }
    );

    track.addEventListener(
      'touchmove',
      function (event) {
        if (!touching) return;

        const dx = event.touches[0].clientX - startX;
        const dy = event.touches[0].clientY - startY;

        if (!swiping && Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) {
          swiping = true;
        }

        if (swiping) {
          // Dış ürün sıralamasının / sayfanın yatay kaymasını engelle
          event.preventDefault();
        }
      },
      { passive: false }
    );

    function endTouch(event) {
      if (!touching) return;
      touching = false;

      if (!swiping) return;
      swiping = false;

      const endX =
        event.changedTouches && event.changedTouches[0]
          ? event.changedTouches[0].clientX
          : startX;
      const dx = endX - startX;

      if (Math.abs(dx) >= SWIPE_THRESHOLD) {
        goTo(dx < 0 ? current + 1 : current - 1);

        // Swipe sonrası ürün linkine gitmeyi engelle
        track.dataset.wasSwiped = 'true';
        window.setTimeout(function () {
          delete track.dataset.wasSwiped;
        }, 0);
      }
    }

    track.addEventListener('touchend', endTouch);
    track.addEventListener('touchcancel', endTouch);

    /* Swipe sonrası tıklama → ürün sayfasına gitmeyi engelle (tap ise gider) */
    if (media) {
      media.addEventListener(
        'click',
        function (event) {
          if (track.dataset.wasSwiped === 'true') {
            event.preventDefault();
            event.stopPropagation();
          }
        },
        true
      );
    }
  }

  function initAll(root) {
    const scope = root || document;
    const cards = scope.querySelectorAll(CARD);
    for (let i = 0; i < cards.length; i++) {
      initCard(cards[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Tema editöründe section yeniden yüklenince yeni kartları da başlat
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target || document);
  });
})();